import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const services = [
  { title: 'Sea Freight', description: 'Full container load (FCL) and less than container load (LCL) ocean freight services from all major Chinese ports to East African destinations — competitive rates, reliable schedules, and end-to-end management.', icon: 'ri-ship-2-line' },
  { title: 'Air Freight', description: 'Express and consolidated air freight solutions for time-sensitive cargo — direct and transit options from China&apos;s major airports to Nairobi, Dar es Salaam, and beyond.', icon: 'ri-flight-takeoff-line' },
  { title: 'Road Freight', description: 'Cross-border and domestic road transportation across East Africa — covering Kenya, Tanzania, Uganda, Rwanda, Burundi, DRC, and South Sudan with reliable fleet partners.', icon: 'ri-truck-line' },
  { title: 'Rail Freight', description: 'Cost-effective rail freight solutions for bulk cargo on key corridors including the Standard Gauge Railway (SGR) from Mombasa to Nairobi and beyond.', icon: 'ri-train-line' },
  { title: 'Project Cargo', description: 'Specialized handling of oversized, heavy-lift, and complex project cargo — route surveys, permit management, specialized equipment, and dedicated project management.', icon: 'ri-anchor-line' },
  { title: 'Cargo Insurance', description: 'Comprehensive marine cargo insurance coverage protecting your shipments against loss, damage, and delay — with competitive premiums and streamlined claims processing.', icon: 'ri-shield-check-line' },
];

const routes = [
  { from: 'Shanghai', to: 'Mombasa', transit: '22-28 days', mode: 'Sea' },
  { from: 'Guangzhou', to: 'Dar es Salaam', transit: '20-26 days', mode: 'Sea' },
  { from: 'Guangzhou', to: 'Nairobi', transit: '3-5 days', mode: 'Air' },
  { from: 'Shanghai', to: 'Nairobi', transit: '3-5 days', mode: 'Air' },
  { from: 'Mombasa', to: 'Nairobi', transit: '1-2 days', mode: 'Road / Rail' },
  { from: 'Dar es Salaam', to: 'Kampala', transit: '3-4 days', mode: 'Road' },
];

export default function FreightForwardingPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Hero */}
        <section className="relative w-full bg-primary-900 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=Dramatic%20aerial%20view%20of%20massive%20container%20ship%20at%20sea%20loaded%20with%20colorful%20cargo%20containers%2C%20global%20shipping%20and%20freight%20transportation%2C%20cinematic%20wide%20angle%20shot%2C%20golden%20sunset%20lighting%2C%20professional%20maritime%20photography&width=1920&height=800&seq=freight-hero&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/90 via-primary-950/80 to-primary-950/95"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16 md:py-24">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Services</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-5 leading-tight">
              Freight Forwarding
            </h1>
            <p className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed mb-8">
              Global freight forwarding services across sea, air, road, and rail — ensuring your cargo moves efficiently through every international border with competitive rates and reliable transit times.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Request a Freight Quote <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Overview */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Overview</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Your Cargo, Our Commitment</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Moving goods between China and East Africa requires expertise across multiple transport modes, regulatory frameworks, and logistical touchpoints. BEKAALI&apos;s freight forwarding division manages this complexity so your cargo arrives on time, every time.
              </p>
            </div>

            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                International freight forwarding is far more than booking shipping space. It demands deep knowledge of shipping routes, carrier relationships, documentation requirements, customs regulations, port operations, and risk management. Our freight forwarding team brings years of combined experience across all these domains.
              </p>
              <p>
                We offer the full spectrum of freight solutions — from full container load (FCL) ocean freight on major China-to-East Africa routes, to express air freight for urgent shipments, to complex multi-modal solutions that combine sea, road, and rail for optimal cost and transit time. Every shipment receives personalized attention with proactive tracking and communication throughout its journey.
              </p>
              <p>
                Our strong relationships with major shipping lines, airlines, and regional transport partners translate into competitive rates, priority space allocation during peak seasons, and reliable capacity even on constrained routes. We don&apos;t just move cargo — we engineer freight solutions that give your business a competitive edge.
              </p>
            </div>
          </div>
        </section>

        {/* Route Table */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Routes</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Key Freight Corridors</h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
                Established routes with proven transit times and reliable schedules.
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-foreground-400">Origin</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-foreground-400">Destination</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-foreground-400">Mode</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider text-foreground-400">Transit Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map((route, i) => (
                      <tr key={i} className="border-b border-background-100 last:border-0 hover:bg-background-100 transition-colors duration-200">
                        <td className="px-6 py-4 text-foreground-800 font-medium">{route.from}</td>
                        <td className="px-6 py-4 text-foreground-800 font-medium">{route.to}</td>
                        <td className="px-6 py-4">
                          <span className="inline-block text-xs font-medium bg-accent-50 text-accent-600 px-2.5 py-1 rounded-full">{route.mode}</span>
                        </td>
                        <td className="px-6 py-4 text-foreground-600">{route.transit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Capabilities</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Freight Services</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {services.map((svc, i) => (
                <div key={i} className="group bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-accent-200/50 transition-all duration-400">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-400">
                    <i className={`${svc.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{svc.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-primary-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">Ship With Confidence</h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-8">
              Get a competitive freight quote for your next shipment. Tell us your cargo details and destination — we&apos;ll handle the rest.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Get a Freight Quote <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}